import { Box, IconButton, styled, useTheme, useMediaQuery } from "@mui/material";
import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Breadcrumb } from "./Breadcrumb";
import { BreadcrumbsGroup } from "src/components/Breadcrumb/BreadcrumbsGroup";
import { FoodsIconButton, greyTheme } from "@foodpilot/foods";
import { isAppInPDFMode } from "src/utils/rendering";
import type { ComponentAppearance } from "src/components/appearance";

const breadcrumbStyles = {
    padding: "2px 8px",
    background: "#fff",
    border: "1px solid #E7E6E4",
    borderRadius: "8px",
    textDecoration: "none",
    height: "auto",
    minWidth: 0,
    "&:hover": {
        borderColor: "#393A36",
        textDecoration: "none",
    },
    "&:hover .text": {
        color: "#50504D",
    },
};

export const StyledBreadcrumb = styled("button")({
    ...breadcrumbStyles,
    justifyContent: "unset",
    marginBottom: ".5rem",
    width: "fit-content",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
});

const StyledNav = styled("nav")(({ theme }) => ({
    display: "flex",
    minWidth: 0,
    position: "relative",
    [theme.breakpoints.down("md")]: {
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        scrollBehavior: "smooth",
        // Hide scrollbar on mobile while keeping functionality
        "&::-webkit-scrollbar": {
            display: "none",
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
    },
    "& ol": {
        display: "flex",
        width: "100%",
        minWidth: 0,
        listStyle: "none",
        margin: 0,
        padding: 0,
        "& li": {
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            flexShrink: 1,
            "& svg": {
                margin: "0 .25rem",
            },
            "& .breadcrumb": {
                ...breadcrumbStyles,
                [theme.breakpoints.down("md")]: {
                    padding: "1px 6px",
                },
            },
            "& .breadcrumb-group": {
                borderRadius: "4px",
                padding: "0 4px",
            },
            "&:last-child": {
                minWidth: 0,
                flexShrink: 1,
                "& > .MuiButtonBase-root": {
                    pointerEvents: "none",
                },
                "& > svg": {
                    display: "none",
                },
                "& > .breadcrumb:hover": {
                    cursor: "default",
                    border: `1px solid ${greyTheme[500]}`,
                },
                "& > .breadcrumb > span": {
                    color: greyTheme[2800],
                    fontWeight: 600,
                },
            },
        },
    },
}));

export type BreadcrumbType = {
    label: string;
    action?: () => void;
    disabled?: boolean;
};

type BreadcrumbsProps = {
    items: BreadcrumbType[];
    compact?: boolean;
    appearance?: ComponentAppearance;
};
export const Breadcrumbs = (props: BreadcrumbsProps) => {
    const { items, compact = false, appearance = "legacy" } = props;
    const { t } = useTranslation();
    const isProductUxV2 = appearance === "product-v2";
    const breadcrumbsSliceAfter = 4;
    const theme = useTheme();
    const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
    const isMobile = compact || isBelowMd;
    const navRef = useRef<HTMLElement>(null);

    const isPdfMode = isAppInPDFMode();
    const createSlice = (breadcrumbs: BreadcrumbType[] | undefined) => {
        if (!breadcrumbs) {
            return [];
        }

        if (breadcrumbs.length <= breadcrumbsSliceAfter) {
            return breadcrumbs;
        }

        return [...breadcrumbs.slice(0, 1), breadcrumbs.slice(1, breadcrumbs.length - 2), ...breadcrumbs.slice(-2)];
    };

    const breadcrumbs = createSlice(items);

    const getKey = (item: BreadcrumbType, index: number) => {
        return `breadcrumb-${item.label}-${index}`;
    };

    useEffect(() => {
        if (isPdfMode) return;
        if (!isMobile || !navRef.current) return;

        const autoScroll = () => {
            if (navRef.current) {
                const hasOverflow = navRef.current.scrollWidth > navRef.current.offsetWidth;

                // Auto-scroll to the end (last breadcrumb) when items change
                if (hasOverflow) {
                    navRef.current.scrollLeft = navRef.current.scrollWidth;
                }
            }
        };

        // Check immediately
        autoScroll();

        // Also check after a small delay to account for rendering
        const timeoutId = setTimeout(autoScroll, 100);

        return () => clearTimeout(timeoutId);
    }, [items, isMobile, isPdfMode]);

    if (isPdfMode) {
        return null;
    }

    // Mobile: show only last breadcrumb with back chevron
    if (isMobile && items.length > 0) {
        const lastBreadcrumb = items[items.length - 1];
        const previousBreadcrumb = items.length > 1 ? items[items.length - 2] : null;

        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                }}
            >
                {previousBreadcrumb &&
                    (isProductUxV2 ?
                        <FoodsIconButton
                            icon="arrowLeft"
                            size="m"
                            buttonProps={{
                                "aria-label": t("common.actions.back", "Back"),
                                disabled: previousBreadcrumb.disabled,
                                onClick: previousBreadcrumb.action,
                                sx: {
                                    flexShrink: 0,
                                    "& svg": { height: theme.spacing(3.5), width: theme.spacing(3.5) },
                                },
                            }}
                        />
                    :   <IconButton
                            aria-label={t("common.actions.back", "Back")}
                            disabled={previousBreadcrumb.disabled}
                            onClick={previousBreadcrumb.action}
                            sx={{
                                flexShrink: 0,
                                padding: theme.spacing(0.5),
                                "&:hover": { backgroundColor: theme.palette.action.hover },
                            }}
                        >
                            <ChevronLeftIcon sx={{ fontSize: theme.spacing(3.5) }} />
                        </IconButton>)}
                <Box
                    sx={{
                        padding: "2px 8px",
                        background: "#fff",
                        border: "1px solid #E7E6E4",
                        borderRadius: "8px",
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            color: greyTheme[2800],
                            fontWeight: 600,
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {lastBreadcrumb.label}
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <StyledNav ref={navRef}>
            <Box component="ol">
                {breadcrumbs?.map((breadcrumb, index) => {
                    if (Array.isArray(breadcrumb)) {
                        return <BreadcrumbsGroup key="breadcrumb-group" breadcrumbs={breadcrumb} />;
                    }

                    return (
                        breadcrumb.label?.length > 0 && (
                            <Breadcrumb key={getKey(breadcrumb, index)} breadcrumb={breadcrumb} />
                        )
                    );
                })}
            </Box>
        </StyledNav>
    );
};
